import { canShowPDPSystem } from '@/utils/canShowPDPSystem';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { returnSpecificImageUrl } from '../../../utils/returnSpecificImageUrl';
import { MediaContainer } from 'src/components/ProductMediaRenderer/MediaContainer';
// eslint-disable-next-line no-undef
import Image from 'next/image';
import CardSkeleton from '../Card/CardSkeleton';
import { RootState } from 'src/redux/reducers';

const LazyMotionSection = dynamic(
  () => import('framer-motion').then((mod) => mod.motion.section),
  { ssr: false }
);

const LazyMotionDiv = dynamic(
  () => import('framer-motion').then((mod) => mod.motion.div),
  { ssr: false, loading: () => <CardSkeleton /> }
);

const slideMotionFirst = {
  rest: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.25,
      type: 'tween',
      ease: 'easeInOut',
    },
  },
  hover: {
    x: '-100%',
    opacity: 0,
    transition: {
      duration: 0.25,
      type: 'tween',
      ease: 'easeInOut',
    },
  },
};

const slideMotionSecond = {
  rest: {
    x: '100%',
    opacity: 0,
    transition: {
      duration: 0.25,
      type: 'tween',
      ease: 'easeInOut',
    },
  },
  hover: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.25,
      type: 'tween',
      ease: 'easeInOut',
    },
  },
};

const dissolveMotionFirst = {
  rest: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
    },
  },
  hover: {
    x: 0,
    opacity: 0,
    transition: {
      duration: 0.8,
    },
  },
};

const dissolveMotionSecond = {
  rest: {
    x: 0,
    opacity: 0,
    transition: {
      duration: 0.8,
    },
  },
  hover: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
    },
  },
};

const defaultImageUrl = 'https://cdn.dotpe.in/longtail/store-items/default_image.png';

const ItemImage = (props) => {
  const [animationProperties, setAnimationProperties] = useState({
    first: null,
    second: null,
  });
  const [imgArr, setImgArr] = useState([]);

  const { theme } = useSelector((state: RootState) => state.storeReducer.store);

  useEffect(() => {
    if (props?.categoryConfig?.animation) {
      switch (props?.categoryConfig?.animation) {
        case 'slide': {
          setAnimationProperties({
            first: slideMotionFirst,
            second: slideMotionSecond,
          });
          break;
        }
        case 'pop': {
          setAnimationProperties({
            first: dissolveMotionFirst,
            second: dissolveMotionSecond,
          });
          break;
        }
        default:
          setAnimationProperties({
            first: null,
            second: null,
          });
          break;
      }
    }
  }, [props?.categoryConfig?.animation]);

  useEffect(() => {
    if (props?.productData?.images?.length > 2) {
      const images = props?.productData?.images;
      // if image length is more than 2
      // reduce array length down to 2
      images.length = 2;
      setImgArr(images);
    } else {
      setImgArr(props?.productData?.images);
    }
  }, [props?.productData?.images]);

  const checkForMediaType = (image) => {
    if (image.media_type === 2) return defaultImageUrl;
    else return returnSpecificImageUrl({ theme: theme, item: image });
  };

  const singleImageRender = (image) => {
    return (
      <MediaContainer>
        <div className="tw-h-full tw-w-full tw-bg-contain tw-bg-center tw-bg-no-repeat">
          <Image
            layout="fill"
            objectFit="contain"
            loading="lazy"
            decoding="async"
            src={checkForMediaType(image)}
          />
        </div>
      </MediaContainer>
    );
  };

  const multiImageRender = (imgArr) => {
    return (
      <MediaContainer>
        <LazyMotionSection
          className="image-wrap tw-h-full tw-w-full !tw-bg-transparent"
          initial="rest"
          whileHover="hover"
          animate="rest"
        >
          {!!imgArr?.length &&
            imgArr.map((image, index) => {
              return (
                <LazyMotionDiv
                  key={index}
                  variants={
                    index === 0 ? animationProperties.first : animationProperties.second
                  }
                  className="collection-image"
                >
                  <Image
                    layout="fill"
                    objectFit="contain"
                    loading="lazy"
                    decoding="async"
                    src={checkForMediaType(image)}
                  />
                </LazyMotionDiv>
              );
            })}
        </LazyMotionSection>
      </MediaContainer>
    );
  };

  return (
    <div className="relative w-100 framer-card">
      {imgArr?.length === 1 || !imgArr
        ? singleImageRender(imgArr ? imgArr[0] : { image_url: defaultImageUrl })
        : multiImageRender(imgArr)}
      <div className="banner-type">
        {canShowPDPSystem(theme?.theme_class) ? 'View Product' : 'Quick View'}
      </div>
    </div>
  );
};

export default ItemImage;
